/* global BigInt */
/**
 * Bijective ID encoding: maps small sequential DB IDs to 8-digit numbers.
 * Prevents URL enumeration (e.g. /property/4 → /property/27847405).
 * Backward-compatible: old short IDs still resolve via fallback.
 */

const SECRET = 48271n;       // Lehmer prime — ensures good distribution
const MOD    = 90_000_000n;  // range of encoded values
const SHIFT  = 17_654_321n;  // additive shift
const BASE   = 10_000_000n;  // encoded numbers start at 10,000,000 (8 digits)

function modInverse(a, m) {
  let [r0, r1] = [a, m];
  let [s0, s1] = [1n, 0n];
  while (r1 !== 0n) {
    const q = r0 / r1;
    [r0, r1] = [r1, r0 - q * r1];
    [s0, s1] = [s1, s0 - q * s1];
  }
  return ((s0 % m) + m) % m;
}

const SECRET_INV = modInverse(SECRET, MOD);

/** Encode a real DB id (small integer) into an 8-digit URL-safe string. */
export function encodeId(id) {
  const n = Number(id);
  if (!Number.isInteger(n) || n <= 0) return String(id);
  const encoded = (BigInt(n) * SECRET + SHIFT) % MOD + BASE;
  return encoded.toString();
}

/** Decode an 8-digit encoded ID back to the real DB id.
 *  Falls back to treating the value as a direct ID for backward-compat.
 */
export function decodeId(encoded) {
  const num = parseInt(String(encoded).trim(), 10);
  if (!Number.isFinite(num) || num <= 0) return null;

  const big = BigInt(num);

  // Encoded range: [BASE, BASE + MOD)
  if (big >= BASE && big < BASE + MOD) {
    const shifted = big - BASE;
    const id = ((shifted - SHIFT + MOD) % MOD * SECRET_INV) % MOD;
    const result = Number(id);
    if (result > 0) return result;
  }

  // Backward-compat: short IDs that predate encoding
  if (num > 0 && num < Number(BASE)) return num;

  return null;
}
